import { useQuery } from "@tanstack/react-query";
import { useAuthReady } from "hooks/useAuthReady";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { fetchScope } from "./schoolReportsApi";

/**
 * Who is this admin and what may they see?
 *
 * `GET /school-reports/scope` answers with
 * `{ role: "school_admin" | "district_admin", client_id, client_name,
 *    school_id, school_name }` and 403s for everyone else. The fetch only
 * happens once the `school_reports` flag is on, so regular teachers never
 * pay for the round trip; a 403 (or any failure) reads as "no scope" and the
 * gated surfaces stay hidden.
 */
export const useSchoolReportScope = () => {
  const authReady = useAuthReady();
  const flagOn = useFeatureFlag("schoolReports");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["school-report-scope"],
    // A 403 is the expected answer for non-admins, so it must resolve as the
    // cached "no scope" value. Left as an error, react-query refetches on
    // every mount, and the landing route unmounts the sidebar while the
    // refetch is in flight — an unmount/refetch loop that blanks the page.
    queryFn: async () => {
      try {
        return await fetchScope();
      } catch (e) {
        if (e?.status === 403) return null;
        throw e;
      }
    },
    enabled: authReady && flagOn,
    // Scope changes when someone edits the user, not mid-session.
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    retryOnMount: false,
  });

  if (!flagOn) return { scope: null, loading: false };
  return {
    scope: isError ? null : data || null,
    loading: isLoading || !authReady,
  };
};
